import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';

/* ── A transfer would replace an existing file ─────────────────────────── */

/**
 * "This file already exists" — asked once per clashing file, BEFORE the batch
 * starts (see utils/transferConflicts.js for why not mid-queue).
 *
 * **Skip holds initial focus, and no way out that is not a click on Replace
 * ever replaces**: Escape, the close button and the backdrop are a skip — or,
 * in a folder merge, Cancel (the whole upload, as closing Explorer's "Replace
 * or Skip" dialog does; owner rule: behave like other apps, 07.10.2026). Both
 * leave the server as it was. Replacing is the one answer here that destroys
 * something, so it must never be what a stray Enter or a dismissed dialog
 * produces. The "... all" buttons only appear when more clashes remain, and
 * say how many they cover.
 */
export default function TransferConflictModal({ conflict, onAnswer }) {
  const { t } = useTranslation();
  const skipRef = useRef(null);
  const { name, remaining, kind, cancellable } = conflict;

  useEffect(() => {
    skipRef.current?.focus();
  }, [name]);

  const skip = () => onAnswer({ action: 'skip' });
  const cancel = () => onAnswer({ action: 'cancel' });
  return (
    <Modal
      isOpen
      onClose={cancellable ? cancel : skip}
      title={t('ftp.conflictTitle')}
      type="warning"
      // A folder merge has five answers; the wider dialog keeps them on one row.
      size={cancellable ? 'lg' : 'md'}
    >
      <div className="space-y-3">
        <p className="ftp-delete-question">
          {kind === 'upload'
            ? t('ftp.conflictOnServer', { name })
            : t('ftp.conflictOnComputer', { name })}
        </p>
        <p className="ftp-modal-note">{t('ftp.conflictReplaceWarning')}</p>
        <div className="modal-footer-row ftp-conflict-actions">
          {cancellable && (
            // A folder merge only: stop the whole upload. Asked before anything
            // moved, so Cancel leaves the server exactly as it was.
            <button
              type="button"
              onClick={cancel}
              className="ftp-conflict-cancel px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
            >
              {t('ftp.conflictCancel')}
            </button>
          )}
          {remaining > 1 && (
            <button
              type="button"
              onClick={() => onAnswer({ action: 'skip', all: true })}
              className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
            >
              {t('ftp.conflictSkipAll', { count: remaining })}
            </button>
          )}
          {remaining > 1 && (
            <button
              type="button"
              onClick={() => onAnswer({ action: 'replace', all: true })}
              className="px-3 py-1.5 text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors text-sm"
            >
              {t('ftp.conflictReplaceAll', { count: remaining })}
            </button>
          )}
          <button
            type="button"
            ref={skipRef}
            onClick={skip}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('ftp.conflictSkip')}
          </button>
          <button
            type="button"
            onClick={() => onAnswer({ action: 'replace' })}
            className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            {t('ftp.conflictReplace')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
